import { FormEvent, useEffect, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import { Modal } from '@/components/ui/Modal';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { CustomerApi } from '@/api';
import { useToast } from '@/hooks/useToast';
import type { Customer, CustomerType, Currency } from '@/types';

const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/;
const INDIAN_STATES: { name: string; code: string }[] = [
  { name: 'Andhra Pradesh', code: '37' },
  { name: 'Assam', code: '18' },
  { name: 'Bihar', code: '10' },
  { name: 'Chhattisgarh', code: '22' },
  { name: 'Delhi', code: '07' },
  { name: 'Goa', code: '30' },
  { name: 'Gujarat', code: '24' },
  { name: 'Haryana', code: '06' },
  { name: 'Himachal Pradesh', code: '02' },
  { name: 'Jammu and Kashmir', code: '01' },
  { name: 'Jharkhand', code: '20' },
  { name: 'Karnataka', code: '29' },
  { name: 'Kerala', code: '32' },
  { name: 'Madhya Pradesh', code: '23' },
  { name: 'Maharashtra', code: '27' },
  { name: 'Manipur', code: '14' },
  { name: 'Meghalaya', code: '17' },
  { name: 'Mizoram', code: '15' },
  { name: 'Nagaland', code: '13' },
  { name: 'Odisha', code: '21' },
  { name: 'Punjab', code: '03' },
  { name: 'Rajasthan', code: '08' },
  { name: 'Sikkim', code: '11' },
  { name: 'Tamil Nadu', code: '33' },
  { name: 'Telangana', code: '36' },
  { name: 'Tripura', code: '16' },
  { name: 'Uttar Pradesh', code: '09' },
  { name: 'Uttarakhand', code: '05' },
  { name: 'West Bengal', code: '19' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the newly-created customer (or updated one) — caller can use this to auto-select it. */
  onSaved?: (customer: { id: string }) => void;
  /** Pre-filled values for edit mode. */
  initial?: Partial<Customer>;
}

export function CustomerFormModal({ open, onClose, onSaved, initial }: Props) {
  const { push } = useToast();
  const isEdit = !!initial?.id;

  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [name, setName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [designation, setDesignation] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [country, setCountry] = useState('India');
  const [customerType, setCustomerType] = useState<CustomerType>('domestic');
  const [currency, setCurrency] = useState<Currency>('INR');
  const [state, setState] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [gstin, setGstin] = useState('');

  // Re-prime form on open
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setName(initial?.name ?? '');
    setContactPerson(initial?.contact_person ?? '');
    setDesignation(initial?.designation ?? '');
    setEmail(initial?.email ?? '');
    setPhone(initial?.phone ?? '');
    setWhatsapp(initial?.whatsapp ?? '');
    setBillingAddress(initial?.billing_address ?? '');
    setShippingAddress(initial?.shipping_address ?? '');
    setCountry(initial?.country ?? 'India');
    setCustomerType((initial?.customer_type as CustomerType) ?? 'domestic');
    setCurrency((initial?.currency_default as Currency) ?? 'INR');
    setState(initial?.state ?? '');
    setStateCode(initial?.state_code ?? '');
    setGstin(initial?.gstin ?? '');
  }, [open, initial]);

  // Auto-link customer_type ↔ country / currency for sane defaults.
  useEffect(() => {
    if (customerType === 'domestic') {
      if (currency !== 'INR') setCurrency('INR');
      if (country.toLowerCase() !== 'india') setCountry('India');
    } else {
      if (country.toLowerCase() === 'india') setCountry('');
      if (currency === 'INR') setCurrency('USD');
    }
    // intentionally not depending on country/currency — only re-run on customerType
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerType]);

  // When state is picked from the dropdown, auto-fill state_code.
  function onStateChange(s: string) {
    setState(s);
    const match = INDIAN_STATES.find((x) => x.name === s);
    setStateCode(match?.code ?? '');
  }

  // Upload a business card / letterhead / document photo and auto-fill the
  // fields below from it. Purely additive — manual entry still works as-is,
  // and everything extracted stays editable before saving.
  function applyExtracted(x: {
    name: string | null; contact_person: string | null; designation: string | null;
    email: string | null; phone: string | null; whatsapp: string | null;
    billing_address: string | null; state: string | null; state_code: string | null;
    gstin: string | null; country: string | null;
  }) {
    if (x.name) setName(x.name);
    if (x.contact_person) setContactPerson(x.contact_person);
    if (x.designation) setDesignation(x.designation);
    if (x.email) setEmail(x.email);
    if (x.phone) setPhone(x.phone);
    if (x.whatsapp) setWhatsapp(x.whatsapp);
    if (x.billing_address) setBillingAddress(x.billing_address);
    if (x.gstin) setGstin(x.gstin.toUpperCase());
    if (x.country) {
      setCustomerType(x.country.trim().toLowerCase() === 'india' ? 'domestic' : 'export');
      setCountry(x.country);
    }
    if (x.state) {
      const match = INDIAN_STATES.find((s) => s.name.toLowerCase() === x.state!.trim().toLowerCase());
      setState(match?.name ?? x.state);
      setStateCode(match?.code ?? x.state_code ?? '');
    }
  }

  async function handlePhotoSelected(file: File) {
    setExtracting(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const extracted = await CustomerApi.extractFromImage(dataUrl);
      applyExtracted(extracted);
      push({ tone: 'success', title: 'Extracted from photo', body: 'Review the fields below before saving.' });
    } catch (err: any) {
      push({ tone: 'error', title: 'Photo extraction failed', body: err?.message ?? 'Unknown error' });
    } finally {
      setExtracting(false);
    }
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Customer name is required.';
    if (!billingAddress.trim()) e.billing_address = 'Billing address is required.';
    if (!country.trim()) e.country = 'Country is required.';
    if (customerType === 'domestic') {
      if (!state) e.state = 'State is required for domestic customers.';
      if (!gstin.trim()) e.gstin = 'GSTIN is required for domestic customers.';
      else if (!GSTIN_RE.test(gstin.trim().toUpperCase()))
        e.gstin = 'GSTIN format is invalid (e.g. 33AABCS1234N1Z5).';
      if (currency !== 'INR') e.currency_default = 'Domestic customers must default to INR.';
    } else {
      if (country.toLowerCase() === 'india')
        e.country = 'Export customers cannot be in India.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        contact_person: contactPerson.trim() || null,
        designation: designation.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        whatsapp: whatsapp.trim() || null,
        billing_address: billingAddress.trim(),
        shipping_address: shippingAddress.trim() || null,
        state: customerType === 'domestic' ? state : null,
        state_code: customerType === 'domestic' ? stateCode : null,
        gstin: customerType === 'domestic' ? gstin.trim().toUpperCase() : null,
        country: country.trim(),
        customer_type: customerType,
        currency_default: currency,
        active: true,
      };
      if (isEdit && initial?.id) {
        await CustomerApi.update(initial.id, body);
        push({ tone: 'success', title: 'Customer updated' });
        onSaved?.({ id: initial.id });
      } else {
        const r = await CustomerApi.create(body);
        push({ tone: 'success', title: 'Customer created', body: name });
        onSaved?.({ id: r.id });
      }
      onClose();
    } catch (err: any) {
      push({ tone: 'error', title: 'Failed to save customer', body: err?.message ?? 'Unknown error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit customer' : 'New customer'}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="customer-form"
            className="btn-primary"
            disabled={busy}
          >
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create customer'}
          </button>
        </>
      }
    >
      <form id="customer-form" onSubmit={onSubmit} className="space-y-3">
        <div className="flex items-center justify-between gap-3 border border-dashed border-line rounded-lg p-3">
          <div className="text-sm text-muted">
            Upload a business card, letterhead, or document photo to auto-fill the fields below.
          </div>
          <label className={`btn-secondary whitespace-nowrap ${extracting ? 'opacity-60' : 'cursor-pointer'}`}>
            {extracting ? 'Extracting…' : 'Upload photo'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={extracting}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handlePhotoSelected(file);
                e.target.value = '';
              }}
            />
          </label>
        </div>

        {/* Type / currency / country */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="label">Type *</label>
            <select
              className="input mt-1"
              value={customerType}
              onChange={(e) => setCustomerType(e.target.value as CustomerType)}
            >
              <option value="domestic">Domestic (India)</option>
              <option value="export">Export</option>
            </select>
          </div>
          <div>
            <label className="label">Currency *</label>
            <select
              className={`input mt-1 ${errors.currency_default ? 'input-error' : ''}`}
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
              disabled={customerType === 'domestic'}
            >
              <option value="INR">INR</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
            {errors.currency_default && (
              <div className="text-xs text-rdx mt-1">{errors.currency_default}</div>
            )}
          </div>
          <div>
            <label className="label">Country *</label>
            <input
              className={`input mt-1 ${errors.country ? 'input-error' : ''}`}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={customerType === 'domestic'}
            />
            {errors.country && (
              <div className="text-xs text-rdx mt-1">{errors.country}</div>
            )}
          </div>
        </div>

        <div>
          <label className="label">Company / Customer name *</label>
          <input
            className={`input mt-1 ${errors.name ? 'input-error' : ''}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="e.g. Cherish Knits Pvt. Ltd."
          />
          {errors.name && <div className="text-xs text-rdx mt-1">{errors.name}</div>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Contact person</label>
            <input
              className="input mt-1"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Designation</label>
            <input
              className="input mt-1"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="label">Email</label>
          <input
            type="email"
            className="input mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Phone</label>
            <PhoneInput
              international
              defaultCountry="IN"
              value={phone}
              onChange={(v) => setPhone(v ?? '')}
              className="phone-input mt-1"
            />
          </div>
          <div>
            <label className="label">WhatsApp</label>
            <PhoneInput
              international
              defaultCountry="IN"
              value={whatsapp}
              onChange={(v) => setWhatsapp(v ?? '')}
              className="phone-input mt-1"
            />
          </div>
        </div>

        <div>
          <label className="label">Billing address *</label>
          <textarea
            className={`input mt-1 min-h-[64px] py-2 ${errors.billing_address ? 'input-error' : ''}`}
            value={billingAddress}
            onChange={(e) => setBillingAddress(e.target.value)}
            rows={2}
          />
          {errors.billing_address && (
            <div className="text-xs text-rdx mt-1">{errors.billing_address}</div>
          )}
        </div>

        <div>
          <label className="label">Shipping address (if different)</label>
          <textarea
            className="input mt-1 min-h-[64px] py-2"
            value={shippingAddress}
            onChange={(e) => setShippingAddress(e.target.value)}
            rows={2}
            placeholder="Leave blank to use billing address."
          />
        </div>

        {customerType === 'domestic' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="label">State *</label>
              <SearchableSelect
                className={`mt-1 ${errors.state ? 'input-error' : ''}`}
                value={state}
                onChange={onStateChange}
                placeholder="Select…"
                options={INDIAN_STATES.map((s) => ({
                  value: s.name,
                  label: s.name,
                  sublabel: `Code ${s.code}`,
                }))}
              />
              {errors.state && (
                <div className="text-xs text-rdx mt-1">{errors.state}</div>
              )}
            </div>
            <div>
              <label className="label">State code</label>
              <input
                className="input mt-1"
                value={stateCode}
                onChange={(e) => setStateCode(e.target.value)}
                disabled
              />
            </div>
          </div>
        )}

        {customerType === 'domestic' && (
          <div>
            <label className="label">GSTIN *</label>
            <input
              className={`input mt-1 font-mono uppercase ${errors.gstin ? 'input-error' : ''}`}
              value={gstin}
              onChange={(e) => setGstin(e.target.value.toUpperCase())}
              maxLength={15}
              placeholder="e.g. 33AABCS1234N1Z5"
            />
            {errors.gstin && <div className="text-xs text-rdx mt-1">{errors.gstin}</div>}
          </div>
        )}
      </form>
    </Modal>
  );
}
